import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, ArrowUpRight, Users } from 'lucide-react';
import api from '../../lib/api';

/**
 * Slim marketing strip on the Home page.
 * Announces the number of agencies "in orbit" and links to the /agencias directory.
 * Renders nothing until data arrives (keeps the layout stable).
 */
export default function AgencyOrbitBanner() {
  const [agencies, setAgencies] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/public/agencies?limit=50')
      .then((res) => {
        if (mounted) setAgencies(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => { if (mounted) setAgencies([]); });
    return () => { mounted = false; };
  }, []);

  if (!agencies || agencies.length === 0) return null;

  const count = agencies.length;
  const totalRoster = agencies.reduce((acc, a) => acc + (a.roster_count || 0), 0);
  // Show up to 5 agency logos (or placeholder circle with initial) as a "stack"
  const stack = agencies.slice(0, 5);

  return (
    <section className="relative py-8 sm:py-10 px-4 sm:px-6 lg:px-8" data-testid="home-agency-orbit-banner">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/agencias"
          className="group flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8 rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 bg-gradient-to-r from-[#0A0A0A] via-[#0D0D0D] to-[#0A0A0A] px-5 sm:px-7 py-5 sm:py-6 transition-colors"
          data-testid="home-agency-orbit-cta"
        >
          {/* Logo stack + count */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex -space-x-3">
              {stack.map((a, idx) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border-2 border-[#0A0A0A] bg-[#121212] overflow-hidden flex items-center justify-center"
                  style={{ zIndex: stack.length - idx }}
                >
                  {a.photo ? (
                    <img src={a.photo} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span className="text-[#D4AF37] text-xs font-black">
                      {(a.name || 'A').charAt(0).toUpperCase()}
                    </span>
                  )}
                </motion.div>
              ))}
            </div>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
              <Building2 size={12} className="text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[10px] sm:text-xs font-black uppercase tracking-widest">
                {count} agencia{count === 1 ? '' : 's'} en órbita
              </span>
            </div>
          </div>

          {/* Copy */}
          <div className="flex-1 min-w-0">
            <p
              className="text-[10px] sm:text-xs uppercase tracking-[0.3em] font-bold text-white/40 mb-1"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              Agencias en Bravo
            </p>
            <h3
              className="text-xl sm:text-2xl md:text-3xl font-black text-white leading-tight tracking-tight group-hover:text-[#D4AF37] transition-colors"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              Descubrí quiénes representan a los artistas.
            </h3>
            <p className="text-white/50 text-xs sm:text-sm mt-1.5 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <Users size={12} className="text-[#D4AF37]" />
                {totalRoster} DJ{totalRoster === 1 ? '' : 's'} bajo representación
              </span>
              <span className="text-white/20">·</span>
              <span>Rosters, contactos, bookings.</span>
            </p>
          </div>

          {/* CTA arrow */}
          <div className="flex items-center gap-2 text-[#D4AF37] font-bold text-sm whitespace-nowrap group-hover:gap-3 transition-all">
            <span
              className="uppercase tracking-widest text-[11px]"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              Ver directorio
            </span>
            <span className="w-9 h-9 rounded-full bg-[#D4AF37] text-black flex items-center justify-center group-hover:rotate-[-45deg] transition-transform duration-500">
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
}
